import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import type { MeasurementSystemEnum } from '@inter-ikea-kompis/enums';
import { InjuryOrDeathWarning, InjuryOrDeathWarningUtility } from '@inter-ikea-kompis/internal';
import type {
	IDexfSettings,
	IFailedShoppingItem,
	IFinancingOption,
	ILinkCard,
	ILocalizedInformation,
	IProduct,
	IShoppingItem,
	IShoppingProduct,
	IStoreAvailability,
	ITheme
} from '@inter-ikea-kompis/types';
import { DexfSettingsUtility, ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import type { IAddToBagAnalyticsProp } from '@inter-ikea-kompis/component-add-to-bag';
import {
	AddToBagModalEnum,
	AddToBagStateEnum,
	UnavailableOnlineBehaviourEnum
} from '@inter-ikea-kompis/component-add-to-bag';
import { AddToBag } from '@inter-ikea-kompis/component-add-to-bag';
import { AddToList } from '@inter-ikea-kompis/component-add-to-list';
import { AddToListLayoutEnum } from '@inter-ikea-kompis/component-add-to-list';
import { BuyInStore, BuyInStoreModalEnum } from '@inter-ikea-kompis/component-buy-in-store';
import type { IFinancialServicesAnalyticsProp } from '@inter-ikea-kompis/component-financial-services';
import { FinancialServices } from '@inter-ikea-kompis/component-financial-services';
import { HyperlinkColorEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { Image } from '@inter-ikea-kompis/component-image';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import {
	PriceModule,
	PriceModuleLayoutTypeEnum,
	PriceModuleModalEnum
} from '@inter-ikea-kompis/component-price-module';
import {
	SaveDesignToProfileModalEnum,
	SaveDesignToProfileStateEnum
} from '@inter-ikea-kompis/component-save-design-to-profile';
import { SaveDesignToProfile } from '@inter-ikea-kompis/component-save-design-to-profile';
import { Text } from '@inter-ikea-kompis/component-text';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import * as SkapaStyle from '@ingka/common-styles-webc';
import { arrowUpFromBase, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import {
	ModalFooter as SkapaModalFooter,
	ModalHeader as SkapaModalHeader,
	Prompt as SkapaPrompt,
	Sheet as SkapaSheet
} from '@ingka/modal-webc/passive.js';

import ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum from '../../enums/ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.js';
import ConfigurationSummaryConfirmationCardStateEnum from '../../enums/ConfigurationSummaryConfirmationCardStateEnum.js';
import ConfigurationSummaryConfirmationCardTypeEnum from '../../enums/ConfigurationSummaryConfirmationCardTypeEnum.js';
import ConfigurationSummaryModalEnum from '../../enums/ConfigurationSummaryModalEnum.js';
import type ConfirmationSummaryCopyDesignCodeStateEnum from '../../enums/ConfirmationSummaryCopyDesignCodeStateEnum.js';
import ConfirmationSummaryKioskMainCTAButtonStateEnum from '../../enums/ConfirmationSummaryKioskMainCTAButtonStateEnum.js';
import ConfirmationSummaryShareDesignStateEnum from '../../enums/ConfirmationSummaryShareDesignStateEnum.js';
import ConfigurationSummaryTracker from '../../trackers/ConfigurationSummaryTracker.js';
import type IConfigurationSummaryAnalyticsProp from '../../types/IConfigurationSummaryAnalyticsProp.js';
import type IConfigurationSummaryBuyInStoreProps from '../../types/IConfigurationSummaryBuyInStoreProps.js';
import type IConfigurationSummaryConfirmationCardAnalyticsProp from '../../types/IConfigurationSummaryConfirmationCardAnalyticsProp.js';
import type IConfigurationSummarySaveDesignToProfileProps from '../../types/IConfigurationSummarySaveDesignToProfileProps.js';
import type IConfigurationSummaryShareDesignCardProps from '../../types/IConfigurationSummaryShareDesignCardProps.js';
import type IConfigurationSummaryTranslations from '../../types/IConfigurationSummaryTranslations.js';
import ConfigurationSummaryCardUtility from '../../utilities/ConfigurationSummaryCardUtility.js';
import ConfigurationSummaryWeightUtility from '../../utilities/ConfigurationSummaryWeightUtility.js';
import ConfigurationSummaryConfirmationCard from '../configuration-summary-confirmation-card/ConfigurationSummaryConfirmationCard.js';
import ConfigurationSummaryShareDesignSheet from '../configuration-summary-share-design-sheet/ConfigurationSummaryShareDesignSheet.js';
import ConfigurationSummaryStyle from './ConfigurationSummaryStyle.js';

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'skapa-sheet': SkapaSheet;
		'skapa-modal-footer': SkapaModalFooter;
		'skapa-modal-header': SkapaModalHeader;
		'skapa-prompt': SkapaPrompt;
	}
}

/**
 *  This is a larger component which consists of the components Add to bag, Price display, What's included, and Save VPC. This component is typically used in a planner's Summary view.
 *
 * @event addToCart Start adding products to shopping cart.
 * @event addToList Start adding products to shopping list.
 * @event addToCartClick User clicks on add to cart button.
 * @event shareDesign When share design is clicked.
 * @event saveDesign When save design is clicked.
 * @event logInAndSaveDesign When login is clicked inside the saveDesignToProfile prompt.
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. (event as CustomEvent).detail.type tells you the type
 * @event addUnavailableProductsToListChange Changes the option "addUnavailableProductsToList". Triggered by user clicking the checkbox
 * @event addUnavailableToCartAndList Start adding unavailable products to shopping cart and shopping list
 * @event copyDesign When copy design is clicked.
 * @event confirmationCardStateChange Triggered when the state for ConfirmationCard changes.
 * @event financialServicesContinueButtonClick Triggered when continue planning button is clicked in financial services sheet.
 * @event financialServicesMoreInfoClick Triggered when read more is clicked in financial services sheet.
 * @event unavailableProductsModalContinue Click on continue in the unavailable products modal.
 * @event goToShoppingBag When goToShoppingBag is clicked.
 * @event resetAddToCartState Sent 3 seconds after addToCartState confirmation to be able to reset the state of the button.
 * @event resetAddToListState Sent 3 seconds after addToListState confirmation to be able to reset the state of the button.
 * @event kioskMainCtaButtonClick Triggered when main call to action button on kiosk is clicked.
 * @event linkCardClick When the link card is clicked.
 * @event shareDesignCardStateChange Triggered when the state for ShareDesignCard changes.
 * @event sendByEmailInput Triggered when the user writes in the sendByEmail view.
 * @event sendByEmailValidation Triggered when the user tries to send with an invalid email.
 * @event sendByEmailSend Triggered when the user clicks send in the sendByEmail view.
 * @event sendByEmailReset Triggered 3 seconds after SendByEmailState is set to success.
 * @event copyDesignCodeClick Triggered when the user clicks the copy design code button.
 * @event copyDesignCodeReset Triggered 3 seconds after copyDesignCodeState is set to success.
 * @event copyDesignLinkClick Triggered when the user clicks the copy design link button.
 * @event copyDesignLinkReset Triggered 3 seconds after copyDesignLinkState is set to success.
 *
 * @slot Description.
 *
 * @tracker packages/component-configuration-summary/src/trackers/ConfigurationSummaryTracker.ts
 */
export default class ConfigurationSummary extends KompisElement {
	// Theme
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Application name for the tip over risk translation key when fixed to ceiling. E.g. "tipOverWarningAlgotPostMountedStorage" where "Algot" is the application name. The key "tipOverWarningPostMountedStorage" will be used if application name is not applied
	@property({ type: String })
	public tipOverRiskApplicationName: string | null = null;

	// Name of the product
	@property({ type: String })
	public productName: string | null = null;

	// List of items with an object format corresponding to { ShoppingItem } from '@inter-ikea-kompis/types';
	@property({ type: Object, required: true })
	public shoppingProducts!: IShoppingProduct[];

	// A list of store availability objects.
	@property({ type: Array })
	public storeAvailabilities: IStoreAvailability[] | null = null;

	// Set to "true" if there is a tip over risk and a warning should be displayed. It will still only be displayed if localization.hasTipOverWarning is set to "true" in the translation tool
	@property({ type: Boolean })
	public tipOverRisk = false;

	// Set to "true" if furniture is fixed to ceiling. This will be used by "tip over warning" disclaimer
	@property({ type: Boolean })
	public furnitureFixedToCeiling = false;

	// True if the price should animate.
	@property({ type: Boolean })
	public animatedPrice = false;

	// VPC code.
	@property({ type: String })
	public vpcCode: string | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IConfigurationSummaryTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IDexfSettings | null = null;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// Disables links. This attribute should be set to "true" when in a Kiosk
	@property({ type: Boolean })
	public disableLinks = false;

	// Shows the share design button.
	@property({ type: Boolean })
	public showShareDesign = false;

	// Shows the save design button.
	@property({ type: Boolean })
	public showSaveDesign = false;

	// True if the add to bag buttons should be disabled
	@property({ type: Boolean })
	public disableAddToBag = false;

	// Shows the add to bag and add to list components. Add to list is also affected for backward compatibility. This attribute should be set to "false" when in a Kiosk. Setting this to "false" also moves the share button down to the text buttons.
	@property({ type: Boolean })
	public showAddToBag = false;

	// Defines the state for add to bag button
	@property({ type: String })
	public addToCartState: AddToBagStateEnum = AddToBagStateEnum.default;

	// Defines the state for add to list button
	@property({ type: String })
	public addToListState: AddToBagStateEnum = AddToBagStateEnum.default;

	// Defines the state for the share design button.
	@property({ type: String })
	public shareDesignState: ConfirmationSummaryShareDesignStateEnum =
		ConfirmationSummaryShareDesignStateEnum.default;

	// Defines the state for the buy in store button (showKioskMainCTAButton).
	@property({ type: String })
	public kioskMainCTAButtonState: ConfirmationSummaryKioskMainCTAButtonStateEnum =
		ConfirmationSummaryKioskMainCTAButtonStateEnum.default;

	// Defines the state for the confirmation card
	@property({ type: String })
	public confirmationCardState: ConfigurationSummaryConfirmationCardStateEnum =
		ConfigurationSummaryConfirmationCardStateEnum.default;

	// Show a modal
	@property({ type: String })
	public visibleModal: ConfigurationSummaryModalEnum | null = null;

	// What type of information to show in the confirmation card modal
	@property({ type: String })
	public confirmationCardType: ConfigurationSummaryConfirmationCardTypeEnum | null = null;

	// Defines the state for copy design code
	@property({ type: String })
	public copyDesignState: ConfirmationSummaryCopyDesignCodeStateEnum | null = null;

	// Defines the state for add to bag button
	@property({ type: String })
	public unavailableOnlineBehaviour: UnavailableOnlineBehaviourEnum =
		UnavailableOnlineBehaviourEnum.blockPurchase;

	// Selects the checkbox for the UnavailableProductsCard that adds unavailable items when user clicks "continue"
	@property({ type: Boolean })
	public addUnavailableProductsToList = false;

	// List of failed shopping products returned when trying to add to cart.
	@property({ type: Object })
	public failedShoppingItems: IFailedShoppingItem[] | null = null;

	// The url for the screenshot.
	@property({ type: String })
	public screenshotUrl: string | null = null;

	// The alt text for the screenshot. If not set, a default alt text will be used.
	@property({ type: String })
	public screenshotAlt: string | null = null;

	// Shows financial services. The financing option is retrieved by service.
	@property({ type: Object })
	public financingOption: IFinancingOption | null = null;

	// Shows total package weight of the shopping products. If using SPR products, includedProducts must also be set.
	@property({ type: Boolean })
	public showTotalPackageWeight = false;

	// A list of included products within SPR products. Must be set (if applicable) for total package weight.
	@property({ type: Object })
	public includedProducts: IProduct[] | null = null;

	// Current locale (e.g. "en-GB"). Must be set to show total package weight and/or to enable analytics.
	@property({ type: String })
	public locale: string | null = null;

	// Shows call to action button to give user the possibility to complete their purchase on kiosk. Won't be shown if showAddToBag is true.
	@property({ type: Boolean })
	public showKioskMainCTAButton = false;

	// Used for automatic analytics. If null or not set, analytics is turned off.
	@property({ type: Object })
	public analytics: IConfigurationSummaryAnalyticsProp | null = null;

	// Displays link card when data and Översätta key showLinkCard are set. For image ratio guidelines, see Skapa Card. Hidden on kiosks when disableLinks is true.
	@property({ type: Object })
	public linkCard: ILinkCard | null = null;

	// A collection of props used for the sheet shown after clicking save design. For more info about the props check the SaveDesignToProfile component.
	@property({ type: Object })
	public saveDesignToProfile: IConfigurationSummarySaveDesignToProfileProps | null = null;

	// Defines the state for the log in and save button.
	@property({ type: String })
	public logInAndSaveState: ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum =
		ConfigurationSummaryConfirmationCardLogInAndSaveStateEnum.default;

	// A collection of props used for the sheet shown after clicking share design. For more info about the props check the ShareDesignCard component.
	@property({ type: Object })
	public shareDesignCard: IConfigurationSummaryShareDesignCardProps | null = null;

	// A collection of props used for the sheet shown after clicking buy in store. For more info about the props check the BuyInStore component.
	@property({ type: Object })
	public buyInStore: IConfigurationSummaryBuyInStoreProps | null = null;

	// Description of the product, e.g. "Wardrobe, 200x300x55 cm". Used in SaveDesignToProfile and ShareDesignCard.
	@property({ type: String })
	public productDescription: string | null = null;

	// Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
	@property({ type: String })
	public measurementSystem: MeasurementSystemEnum | null = null;

	private sheetElement: SkapaSheet | null = null;
	private sheetHeader: SkapaModalHeader | null = null;
	private backButton: SkapaIconButton | null = null;
	private ecoFeeModalHeading: HTMLHeadingElement | null = null;
	private triggerElement: HTMLElement | null = null;
	private tracker: ConfigurationSummaryTracker | null = null;
	private dexfSettings: IDexfSettings | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}
		return html`
			<style>
				${ConfigurationSummaryStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div class="priceHeader">
				<div class="priceHeaderPrice">
					${this.getPriceDisplay()} ${this.getDualCurrencyExchangeRate()}
					${this.getFinancialServices()}
				</div>
				<div class="priceHeaderButtons">
					${this.getShareDesignIconButton()} ${this.getAddToList()}
				</div>
			</div>
			${this.getTipOverWarning()} ${this.getTitle20()} ${this.getInjuryOrDeathWarning()}
			<div class="buttons">
				${this.getKioskMainCTAButton()} ${this.getAddToBag()} ${this.getSaveDesignButton()}
				${this.getShareDesignButton()}
			</div>
			${this.getBuyInStore()} ${this.getConfigurationSummaryShareDesignSheet()}
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.closeConfirmationCardSheet();
		this.disconnectTracker();
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(arrowUpFromBase);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('skapa-sheet', SkapaSheet);
		this.customElements.define('skapa-modal-footer', SkapaModalFooter);
		this.customElements.define('skapa-modal-header', SkapaModalHeader);
		this.customElements.define('skapa-prompt', SkapaPrompt);
		this.customElements.define('kompis-price-module', PriceModule);
		this.customElements.define('kompis-add-to-list', AddToList);
		this.customElements.define('kompis-add-to-bag', AddToBag);
		this.customElements.define('kompis-financial-services', FinancialServices);
		this.customElements.define('kompis-hyperlink', Hyperlink);
		this.customElements.define('kompis-image', Image);
		this.customElements.define('kompis-injury-or-death-warning', InjuryOrDeathWarning);
		this.customElements.define('kompis-save-design-to-profile', SaveDesignToProfile);
		this.customElements.define('kompis-buy-in-store', BuyInStore);
		this.customElements.define('kompis-text', Text);
		this.customElements.define(
			'kompis-configuration-summary-confirmation-card',
			ConfigurationSummaryConfirmationCard
		);
		this.customElements.define(
			'kompis-configuration-summary-share-design-sheet',
			ConfigurationSummaryShareDesignSheet
		);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}
		if (changedProperties.has('visibleModal')) {
			if (this.visibleModal === ConfigurationSummaryModalEnum.confirmationCard) {
				this.openConfirmationCardSheet();
			} else {
				this.closeConfirmationCardSheet();
			}
		} else if (this.sheetElement) {
			this.setPropsToCard(this.sheetElement);
		}
		if (changedProperties.has('analytics')) {
			this.disconnectTracker();
			this.connectTracker();
		}
		if (changedProperties.has('confirmationCardType')) {
			this.sendTrackingData();
		}
		if (
			changedProperties.has('visibleModal') &&
			this.visibleModal === ConfigurationSummaryModalEnum.shareDesignCard
		) {
			this.tracker?.sendSaveDesign({ type: 'Share', vpcCode: this.vpcCode ?? undefined });
		}

		if (changedProperties.has('confirmationCardState')) {
			if (this.confirmationCardState !== ConfigurationSummaryConfirmationCardStateEnum.default) {
				this.showBackButton();
			} else {
				this.hideBackButton();
			}

			if (this.confirmationCardState === ConfigurationSummaryConfirmationCardStateEnum.ecoFeeCard) {
				this.showEcoFeeModalHeading();
			} else {
				this.hideEcoFeeModalHeading();
			}
		}
		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getSettings(): IDexfSettings {
		return <IDexfSettings>this.dexfSettings;
	}

	private getTranslations(): IConfigurationSummaryTranslations {
		const translations = this.translations
			? this.translations
			: <IConfigurationSummaryTranslations>this.localizedInformation?.translations;

		return translations;
	}
	private getSaveDesignButton(): RenderResult {
		if (!this.showSaveDesign || !this.saveDesignToProfile) {
			return null;
		}

		return html`<kompis-save-design-to-profile
			.theme="${this.theme}"
			.translations="${this.translations}"
			.loggedIn="${this.saveDesignToProfile.loggedIn}"
			.saveDesignState="${this.saveDesignToProfile.saveDesignState}"
			.visibleModal="${this.visibleModal && this.visibleModal in SaveDesignToProfileModalEnum
				? this.visibleModal
				: null}"
			@modalClose="${() =>
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: { visibleModal: null }
					})
				)}"
			@modalOpen="${() =>
				this.dispatchEvent(
					new CustomEvent('modalOpen', {
						bubbles: true,
						detail: {
							visibleModal: SaveDesignToProfileModalEnum.saveDesignToProfileCard
						}
					})
				)}"
			@saveDesign="${this.onSaveDesign.bind(this)}"
			@logInAndSaveDesign="${this.onLoginAndSaveDesign.bind(this)}"
		>
			<div class="saveDesign" slot="save-button">
				<skapa-button
					?loading="${this.saveDesignToProfile.saveDesignState ===
					SaveDesignToProfileStateEnum.loading}"
					variant="secondary"
					fluid
					>${this.getTranslations().save}</skapa-button
				>
			</div>
		</kompis-save-design-to-profile>`;
	}

	private onSaveDesign(): void {
		this.dispatchEvent(
			new CustomEvent('saveDesign', {
				bubbles: true
			})
		);
		this.tracker?.sendSaveDesign({ type: 'Save' });
	}

	private onLoginAndSaveDesign(): void {
		this.dispatchEvent(
			new CustomEvent('logInAndSaveDesign', {
				bubbles: true,
				detail: { visibleModal: null }
			})
		);
		this.tracker?.sendSaveDesign({ type: 'Save' });
	}

	private getBuyInStore(): RenderResult {
		return html`<kompis-buy-in-store
			.theme="${this.theme}"
			.localizedInformation="${this.localizedInformation}"
			.settings="${this.settings}"
			.translations="${this.translations}"
			.storeAvailabilities="${this.storeAvailabilities}"
			.buyInStoreLink="${this.buyInStore?.buyInStoreLink}"
			.visibleModal="${this.visibleModal && this.visibleModal in BuyInStoreModalEnum
				? this.visibleModal
				: null}"
			@modalClose="${() =>
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: { visibleModal: null, triggerElement: this.queryKioskMainCTAButton() }
					})
				)}"
		></kompis-buy-in-store>`;
	}

	private getShareDesignButton(): RenderResult {
		if (!this.showShareDesign || (this.showAddToBag && this.showSaveDesign)) {
			return null;
		}

		const secondaryVariant: ButtonVariant = 'secondary';

		return html`
			<div class="shareDesign">
				<skapa-button
					variant="${secondaryVariant}"
					fluid
					?loading="${this.shareDesignState === ConfirmationSummaryShareDesignStateEnum.loading}"
					loadingSrText="${this.getTranslations().loading}"
					@click="${this.onShareDesign.bind(this)}"
				>
					${this.getTranslations().share}
				</skapa-button>
			</div>
		`;
	}

	private getShareDesignIconButton(): RenderResult {
		if (!this.showShareDesign || !this.showAddToBag || !this.showSaveDesign) {
			return null;
		}

		const tertiaryVariant: ButtonVariant = 'tertiary';

		return html`
			<skapa-icon-button
				variant="${tertiaryVariant}"
				?loading="${this.shareDesignState === ConfirmationSummaryShareDesignStateEnum.loading}"
				loadingSrText="${this.getTranslations().loading}"
				@click="${this.onShareDesign.bind(this)}"
				size="small"
			>
				<skapa-icon icon="arrow-up-from-base" slot="icon"></skapa-icon>
				${this.getTranslations().share}
			</skapa-icon-button>
		`;
	}

	private getPriceDisplay(): RenderResult {
		return html`
			<kompis-price-module
				.theme="${this.theme}"
				.shoppingProducts="${this.shoppingProducts}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.productName="${this.productName}"
				.animated="${this.animatedPrice}"
				.disableLinks="${this.disableLinks}"
				.layout="${PriceModuleLayoutTypeEnum.emphasized}"
				.showRegularPriceExclTax="${true}"
				.showEcoFee="${true}"
				.showPriceExclTax="${true}"
				.visibleModal="${this.visibleModal && this.visibleModal in PriceModuleModalEnum
					? this.visibleModal
					: null}"
				disableHeadings
				@modalOpen="${(event: CustomEvent) =>
					this.dispatchEvent(
						new CustomEvent('modalOpen', { bubbles: true, detail: (event as CustomEvent).detail })
					)}"
				@modalClose="${() =>
					this.dispatchEvent(
						new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
					)}"
			>
				<slot></slot>
				${this.getTotalPackageWeight()}
			</kompis-price-module>
		`;
	}

	/**
	 * @returns Total package weight if enabled.
	 */
	private getTotalPackageWeight(): RenderResult {
		if (!this.showTotalPackageWeight || !this.locale) {
			return null;
		}

		const totalPackageWeightKgs = ConfigurationSummaryWeightUtility.getTotalWeightInKg(
			this.shoppingProducts,
			this.includedProducts
		);

		const localizedWeight = ConfigurationSummaryWeightUtility.getLocalizedWeight(
			totalPackageWeightKgs,
			this.locale,
			this.getTranslations(),
			this.getSettings().localisation.useMetricMeasures,
			this.measurementSystem
		);

		const localizedWeightText =
			ConfigurationSummaryWeightUtility.getLocalizedWeightHtmlString(localizedWeight);

		return html`
			<kompis-text
				class="totalPackageWeight"
				.theme="${this.theme}"
				.type="${ThemeFontStyleTypeEnum.bodyLarge}"
			>
				${this.getTranslations().configurationSummaryTotalPackageWeight}: ${localizedWeightText}
			</kompis-text>
		`;
	}

	/**
	 * Returns HTML for exchange rate if more than one currency is provided.
	 *
	 * @returns Template.
	 */
	private getDualCurrencyExchangeRate(): RenderResult {
		const hasDualCurrencies =
			new Set(
				this.shoppingProducts[0]?.product?.content?.priceInformation?.salesPrice.map(
					(price) => price.currencyCode
				)
			).size > 1;

		const { priceSettings } = this.getSettings().kompis;

		if (!(priceSettings.dualCurrencyExchangeRate && hasDualCurrencies)) {
			return null;
		}

		return html`
			<kompis-text
				class="priceExchangeRateContainer"
				.theme="${this.theme}"
				.type="${ThemeFontStyleTypeEnum.bodySmall}"
			>
				${priceSettings.dualCurrencyExchangeRate}
			</kompis-text>
		`;
	}

	private getFinancialServices(): RenderResult {
		if (!this.financingOption) {
			return null;
		}

		return html`
			<kompis-financial-services
				.localizedInformation="${this.localizedInformation}"
				.settings="${this.settings}"
				.translations="${this.translations}"
				.theme="${this.theme}"
				.financingOption="${this.financingOption}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal}"
				.analytics="${this.getSubComponentAnalytics()}"
				@modalClose="${({ detail }) => {
					this.dispatchEvent(
						new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
					);
					detail.triggerElement.focus();
				}}"
				@modalOpen="${() =>
					this.dispatchEvent(
						new CustomEvent('modalOpen', {
							bubbles: true,
							detail: { visibleModal: ConfigurationSummaryModalEnum.financialServicesCard }
						})
					)}"
				@continueButtonClick="${({ detail }) => {
					this.dispatchEvent(
						new CustomEvent('financialServicesContinueButtonClick', { bubbles: true, detail })
					);
					detail.triggerElement.focus();
				}}"
				@moreInfoClick="${({ detail }) =>
					this.dispatchEvent(
						new CustomEvent('financialServicesMoreInfoClick', { bubbles: true, detail })
					)}"
			></kompis-financial-services>
		`;
	}

	private getAddToBag(): RenderResult {
		if (!this.showAddToBag) {
			return null;
		}

		return html`
			<kompis-add-to-bag
				@modalOpen="${({ detail }) =>
					this.dispatchEvent(new CustomEvent('modalOpen', { bubbles: true, detail }))}"
				@modalClose="${({ detail }) => {
					this.dispatchEvent(
						new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
					);
					detail.triggerElement?.focus();
				}}"
				@addUnavailableToCartAndList="${this.onAddUnavailableToCartAndList.bind(this)}"
				@addToCart="${this.onAddToCart.bind(this)}"
				@addToCartClick="${({ detail }) => {
					this.dispatchEvent(
						new CustomEvent('addToCartClick', {
							bubbles: true
						})
					);
					this.triggerElement = detail.triggerElement;
				}}"
				@addUnavailableProductsToListChange="${(event: CustomEvent) =>
					this.dispatchEvent(
						new CustomEvent('addUnavailableProductsToListChange', {
							bubbles: true,
							detail: (event as CustomEvent).detail
						})
					)}"
				@unavailableProductsModalContinue="${() =>
					this.dispatchEvent(
						new CustomEvent('unavailableProductsModalContinue', {
							bubbles: true
						})
					)}}"
				@resetAddToCartState="${() =>
					this.dispatchEvent(
						new CustomEvent('resetAddToCartState', {
							bubbles: true
						})
					)}}"
				@resetAddToListState="${() =>
					this.dispatchEvent(
						new CustomEvent('resetAddToListState', {
							bubbles: true
						})
					)}}"
				.theme="${this.theme}"
				.disabled="${this.disableAddToBag}"
				.addToCartState="${this.addToCartState}"
				.shoppingProducts="${this.shoppingProducts}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal && this.visibleModal in AddToBagModalEnum
					? this.visibleModal
					: null}"
				.showShareDesign="${this.showShareDesign}"
				.localizedInformation="${this.localizedInformation}"
				.settings="${this.settings}"
				.translations="${this.translations}"
				.unavailableOnlineBehaviour="${this.unavailableOnlineBehaviour}"
				.addUnavailableProductsToList="${this.addUnavailableProductsToList}"
				.failedShoppingItems="${this.failedShoppingItems}"
				.analytics="${this.getSubComponentAnalytics()}"
				hideListButton
			>
			</kompis-add-to-bag>
		`;
	}

	private getAddToList(): RenderResult {
		if (!this.showAddToBag) {
			return null;
		}

		const layout = AddToListLayoutEnum.iconSmall;
		const iconVariant: ButtonVariant = 'tertiary';

		return html`
			<kompis-add-to-list
				@addToList="${this.onAddToList.bind(this)}"
				@resetState="${() =>
					this.dispatchEvent(
						new CustomEvent('resetAddToListState', {
							bubbles: true
						})
					)}}"
				.translations="${this.translations}"
				.localizedInformation="${this.localizedInformation}"
				.settings="${this.settings}"
				.disabled="${this.disableAddToBag}"
				.state="${this.addToListState}"
				.shoppingProducts="${this.shoppingProducts}"
				.analytics="${this.getSubComponentAnalytics()}"
				.layout="${layout}"
				.iconVariant="${iconVariant}"
			>
			</kompis-add-to-list>
		`;
	}

	private kioskMainCTAButtonShouldShow(): boolean {
		return this.showKioskMainCTAButton && !this.showAddToBag;
	}

	private getKioskMainCTAButton(): RenderResult {
		if (!this.kioskMainCTAButtonShouldShow()) {
			return null;
		}

		const emphasisedVariant: ButtonVariant = 'emphasised';

		return html`<skapa-button
			id="kiosk-main-cta"
			variant="${emphasisedVariant}"
			fluid
			?loading="${this.kioskMainCTAButtonState ===
			ConfirmationSummaryKioskMainCTAButtonStateEnum.loading}"
			loadingSrText="${this.getTranslations().loading}"
			@click="${this.onKioskMainCTAButtonClick.bind(this)}"
		>
			${this.getTranslations().kioskContinueOnMobileButton}
		</skapa-button>`;
	}

	private onKioskMainCTAButtonClick(): void {
		this.tracker?.sendKioskMainCTAButtonClick();

		this.triggerElement = this.queryKioskMainCTAButton();
		this.dispatchEvent(
			new CustomEvent('kioskMainCtaButtonClick', {
				bubbles: true,
				detail: {
					triggerElement: this.triggerElement
				}
			})
		);
	}

	private queryKioskMainCTAButton(): HTMLButtonElement {
		return <HTMLButtonElement>(
			this.shadowRoot?.querySelector('#kiosk-main-cta')?.shadowRoot?.querySelector('button')
		);
	}

	/**
	 * Get analytics object to pass on to sub components.
	 */
	private getSubComponentAnalytics():
		| (IFinancialServicesAnalyticsProp &
				IAddToBagAnalyticsProp &
				IConfigurationSummaryConfirmationCardAnalyticsProp &
				IConfigurationSummaryAnalyticsProp)
		| null {
		const reporterId = this.tracker?.getConnectedReporterId();
		return this.analytics
			? {
					...this.analytics,
					locale: this.getLocaleForAnalyticsOrThrow(),

					__INTERNAL__reporter: reporterId
						? { connected: true, id: reporterId }
						: { connected: false }
				}
			: null;
	}

	private getLocaleForAnalyticsOrThrow(): string {
		if (!this.locale) {
			throw Error("The property 'locale' must be set when analytics is enabled.");
		}
		return this.locale;
	}

	private getInjuryOrDeathWarning() {
		const products = this.shoppingProducts.map((sp) => sp.product);
		const hasWarning = InjuryOrDeathWarningUtility.anyProductHasWarning(
			products,
			this.includedProducts ?? []
		);
		if (!hasWarning) return null;

		return html`
			<kompis-injury-or-death-warning
				.translations="${this.getTranslations()}"
			></kompis-injury-or-death-warning>
		`;
	}

	private getTipOverWarning(): RenderResult {
		if (!this.getSettings().kompis.localization.hasTipOverWarning || !this.tipOverRisk) {
			return null;
		}

		const tipOverText = this.getTipOverText();

		if (!tipOverText) {
			return null;
		}

		let link: string[] | null = null;

		if (tipOverText.includes('[[')) {
			link = tipOverText.split('[[tip_over_warning_link]]');
		} else {
			link = tipOverText.split('{{tip_over_warning_link}}');
		}

		const list = link.map(
			(text) => html`
				<kompis-text
					.theme="${this.theme}"
					.type="${ThemeFontStyleTypeEnum.bodySmall}"
					class="disclaimer"
					>${text}</kompis-text
				>
			`
		);

		if (this.disableLinks || !this.getSettings().kompis.urls.tipOverWarningUrl || list.length < 2) {
			return list;
		}

		return [
			list[0],
			html`
				<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.bodySmall}">
					<kompis-hyperlink
						class="tipOverWarningLink"
						.href="${this.getSettings().kompis.urls.tipOverWarningUrl}"
						.theme="${this.theme}"
						.target="${'_blank'}"
						.color="${HyperlinkColorEnum.black}"
						.text="${this.getTranslations().tipOverWarningLink}"
					></kompis-hyperlink>
				</kompis-text>
			`,
			list[1]
		];
	}

	private getTipOverText(): string {
		if (this.furnitureFixedToCeiling) {
			return this.getTranslations()[
				`tipOverWarning${this.tipOverRiskApplicationName || ''}PostMountedStorage`
			];
		}
		return this.getTranslations().tipOverWarning;
	}

	private getTitle20(): RenderResult {
		return this.getSettings().kompis.priceSettings.showTitle20 &&
			this.getTranslations().priceInfoTitle20
			? html`
					<kompis-text
						.theme="${this.theme}"
						.type="${ThemeFontStyleTypeEnum.bodySmall}"
						class="disclaimer"
						>${this.getTranslations().priceInfoTitle20}</kompis-text
					>
				`
			: null;
	}

	private onShareDesign(): void {
		this.triggerElement = this.queryShareDesignButton();
		this.dispatchEvent(
			new CustomEvent('shareDesign', {
				bubbles: true,
				detail: {
					shoppingItems: this.getShoppingItems(),
					triggerElement: this.triggerElement
				}
			})
		);
	}

	private queryShareDesignButton(): HTMLButtonElement {
		const button = this.shadowRoot?.querySelector(
			// The button could either be in the main content or the price header
			'.shareDesign skapa-button, .priceHeaderButtons skapa-icon-button'
		);

		return <HTMLButtonElement>button?.shadowRoot?.querySelector('button');
	}

	/**
	 * Event handler.
	 *
	 * @param event Event.
	 */
	private onAddToCart(event: CustomEvent): void {
		this.dispatchEvent(
			new CustomEvent('addToCart', {
				bubbles: true,
				detail: {
					...(event as CustomEvent).detail
				}
			})
		);
	}

	/**
	 * Event handler.
	 *
	 * @param event Event.
	 */
	private onAddToList(event: CustomEvent): void {
		this.dispatchEvent(
			new CustomEvent('addToList', {
				bubbles: true,
				detail: {
					...(event as CustomEvent).detail
				}
			})
		);
		this.triggerElement = (event as CustomEvent).detail.triggerElement;
	}

	/**
	 * Event handler.
	 *
	 * @param event Event.
	 */
	private onAddUnavailableToCartAndList(event: CustomEvent): void {
		this.dispatchEvent(
			new CustomEvent('addUnavailableToCartAndList', {
				bubbles: true,
				detail: {
					...(event as CustomEvent).detail
				}
			})
		);
	}

	private getShoppingItems(): IShoppingItem[] {
		return ProductUtility.getShoppingItems(this.shoppingProducts);
	}

	private closeConfirmationCardSheet(): void {
		if (this.sheetElement) {
			this.sheetElement.open = false;
			this.sheetElement = null;
		}
	}

	private openConfirmationCardSheet(): void {
		if (this.sheetElement) {
			return;
		}
		const sheetElement: SkapaSheet = this.createSheetElement();
		const onClose = (): void => {
			if (this.sheetElement) {
				this.dispatchEvent(
					new CustomEvent('modalClose', {
						bubbles: true,
						detail: {
							visibleModal: null,
							triggerElement: this.triggerElement
						}
					})
				);
				this.dispatchEvent(
					new CustomEvent('confirmationCardStateChange', {
						bubbles: true,
						detail: { selectedState: ConfigurationSummaryConfirmationCardStateEnum.default }
					})
				);
			}
		};

		// When user clicks 'close'
		sheetElement.addEventListener('closerequest', onClose);

		// Remove sheet when it has closed.
		sheetElement.addEventListener('closed', () => {
			sheetElement.parentElement?.removeChild(sheetElement);
		});

		this.sheetElement = sheetElement;
		this.sheetHeader = sheetElement.querySelector(
			this.customElements.getInternalName('skapa-modal-header')
		);
		sheetElement.open = true;
		const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
		parent.appendChild(sheetElement);
	}

	private createSheetElement(): SkapaSheet {
		const sheetElement = this.createSheet();
		const sheetHeader = this.createSheetHeader();
		const configurationSummaryConfirmationCard = this.createInternalElement(
			'kompis-configuration-summary-confirmation-card'
		);

		sheetElement.appendChild(sheetHeader);
		sheetElement.appendChild(configurationSummaryConfirmationCard);

		this.setPropsToCard(sheetElement);
		configurationSummaryConfirmationCard.addEventListener('copyDesign', (event) => {
			this.dispatchEvent(
				new CustomEvent('copyDesign', {
					bubbles: true,
					detail: { ...(event as CustomEvent).detail }
				})
			);
		});
		configurationSummaryConfirmationCard.addEventListener(
			'confirmationCardStateChange',
			(event) => {
				this.dispatchEvent(
					new CustomEvent('confirmationCardStateChange', {
						bubbles: true,
						detail: { ...(event as CustomEvent).detail }
					})
				);
			}
		);
		configurationSummaryConfirmationCard.addEventListener('goToShoppingBag', () => {
			this.dispatchEvent(new CustomEvent('goToShoppingBag', { bubbles: true }));
		});
		configurationSummaryConfirmationCard.addEventListener('linkCardClick', () => {
			this.dispatchEvent(new CustomEvent('linkCardClick', { bubbles: true }));
		});
		configurationSummaryConfirmationCard.addEventListener(
			'logInAndSaveDesign',
			this.onLoginAndSaveDesign.bind(this)
		);

		return sheetElement;
	}

	private createSheet(): SkapaSheet {
		const sheetElement = this.createInternalElement('skapa-sheet');

		sheetElement.size = 'small';
		sheetElement.fullHeight = true;
		sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
		sheetElement.style.zIndex = '500';

		return sheetElement;
	}

	private createSheetHeader(): SkapaModalHeader {
		const sheetHeader = this.createInternalElement('skapa-modal-header');
		sheetHeader.slot = 'header';

		if (this.confirmationCardState !== ConfigurationSummaryConfirmationCardStateEnum.default) {
			this.showBackButton();
		}

		this.ecoFeeModalHeading = this.createEcoFeeModalHeading();
		if (this.confirmationCardState === ConfigurationSummaryConfirmationCardStateEnum.ecoFeeCard) {
			this.showEcoFeeModalHeading();
		}

		const closeButton = this.createCloseButton();
		sheetHeader.appendChild(closeButton);

		sheetHeader.addEventListener('backrequest', () => {
			this.dispatchEvent(
				new CustomEvent('confirmationCardStateChange', {
					bubbles: true,
					detail: { selectedState: ConfigurationSummaryConfirmationCardStateEnum.default }
				})
			);
		});

		return sheetHeader;
	}

	private createBackButton(): SkapaIconButton {
		const backButton = this.createInternalElement('skapa-icon-button');
		backButton.a11yLabel = this.getTranslations().modalAriaLabelButtonBack || '';
		backButton.variant = 'primary';
		backButton.inverse = true;
		backButton.size = 'small';

		const icon = this.createInternalElement('skapa-icon');
		icon.icon = 'arrow-left';
		icon.flipRTL = true;
		icon.slot = 'icon';
		backButton.append(icon);
		backButton.slot = 'back-button';

		return backButton;
	}

	private showBackButton(): void {
		this.backButton = this.createBackButton();
		if (this.sheetHeader) this.sheetHeader.appendChild(this.backButton);
	}

	private hideBackButton(): void {
		if (this.sheetHeader && this.backButton && this.sheetHeader.contains(this.backButton)) {
			this.sheetHeader.removeChild(this.backButton);
		}

		this.backButton = null;
	}

	private createEcoFeeModalHeading(): HTMLHeadingElement {
		const heading = document.createElement('h2');

		heading.innerText = this.getTranslations().ecoHeadline;
		heading.className = SkapaStyle.Text.classnames({ heading: { s: true } });
		heading.style.margin = '0';

		const style = document.createElement('style');
		style.textContent = SkapaStyle.Text.CSS;

		heading.appendChild(style);

		return heading;
	}

	private showEcoFeeModalHeading(): void {
		if (this.sheetHeader && this.ecoFeeModalHeading) {
			this.sheetHeader.appendChild(this.ecoFeeModalHeading);
		}
	}

	private hideEcoFeeModalHeading(): void {
		if (
			this.sheetHeader &&
			this.ecoFeeModalHeading &&
			this.sheetHeader.contains(this.ecoFeeModalHeading)
		) {
			this.sheetHeader.removeChild(this.ecoFeeModalHeading);
		}
	}

	private createCloseButton(): HTMLSpanElement {
		const closeSpan = document.createElement('span');
		closeSpan.slot = 'closebutton-label';
		closeSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;

		return closeSpan;
	}

	private setPropsToCard(sheetElement: SkapaSheet): void {
		const cardElement = sheetElement.querySelector(
			this.customElements.getInternalName('kompis-configuration-summary-confirmation-card')
		);

		if (!cardElement) {
			return;
		}

		cardElement.theme = this.theme;
		cardElement.localizedInformation = this.localizedInformation;
		cardElement.translations = this.translations;
		cardElement.settings = this.settings;
		cardElement.vpcCode = this.vpcCode;
		cardElement.addUnavailableProductsToList = this.addUnavailableProductsToList;
		cardElement.screenshotUrl = this.screenshotUrl;
		cardElement.screenshotAlt = this.screenshotAlt;
		cardElement.shoppingProducts = this.shoppingProducts;
		cardElement.productName = this.productName;
		cardElement.state = this.confirmationCardState;
		cardElement.disableLinks = this.disableLinks;
		cardElement.showSaveDesign = this.showSaveDesign;
		cardElement.loggedIn = this.saveDesignToProfile?.loggedIn ?? false;
		cardElement.logInAndSaveState = this.logInAndSaveState;
		if (this.confirmationCardType) {
			cardElement.type = this.confirmationCardType;
		}
		if (this.copyDesignState) {
			cardElement.copyDesignState = this.copyDesignState;
		}
		if (this.linkCard) {
			cardElement.linkCard = this.linkCard;
		}
		cardElement.analytics = this.getSubComponentAnalytics();
		cardElement.innerHTML = this.innerHTML?.trim();

		this.setSheetAriaLabel(sheetElement);
	}

	private setSheetAriaLabel(sheetElement: SkapaSheet): void {
		if (this.confirmationCardState !== ConfigurationSummaryConfirmationCardStateEnum.ecoFeeCard) {
			sheetElement.setAttribute(
				'aria-label',
				ConfigurationSummaryCardUtility.getSheetBodyTitleText(
					this.getTranslations(),
					this.confirmationCardType
				)
			);
		} else {
			sheetElement.setAttribute('aria-label', this.getTranslations().ecoHeadline);
		}
	}

	private isShoppingProductOnlineSaleable(shoppingProduct: IShoppingProduct): boolean {
		return Boolean(
			shoppingProduct.product.content?.priceInformation?.availableForHomeDelivery ||
				shoppingProduct.product.content?.priceInformation?.availableForClickCollect ||
				this.localizedInformation?.localization.disableOnlineSaleableVerification
		);
	}

	/**
	 * Returns a list of ShoppingProducts that is not saleable online.
	 *
	 * @returns Template.
	 */
	private getUnavailableShoppingProducts(): IShoppingItem[] {
		const shoppingProducts = this.shoppingProducts
			.filter((shoppingProduct) => shoppingProduct.quantity > 0)
			.filter((shoppingProduct) => !this.isShoppingProductOnlineSaleable(shoppingProduct));

		return shoppingProducts.map((shoppingProduct) => ({
			id: shoppingProduct.product.content?.ruItemNo ?? '',
			type: shoppingProduct.product.content?.ruItemType ?? '',
			quantity: shoppingProduct.quantity
		}));
	}

	private sendTrackingData(): void {
		const designId = this.vpcCode ?? undefined;

		if (
			this.confirmationCardType === ConfigurationSummaryConfirmationCardTypeEnum.addToCartSuccess ||
			this.confirmationCardType ===
				ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartAndListSuccess ||
			this.confirmationCardType ===
				ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartSuccessAndListFail
		) {
			const unavailableProducts =
				this.confirmationCardType === ConfigurationSummaryConfirmationCardTypeEnum.addToCartSuccess
					? undefined
					: this.getUnavailableShoppingProducts();
			this.tracker?.sendAddToCart(
				this.getShoppingItems(),
				this.shoppingProducts,
				this.getSettings(),
				unavailableProducts,
				designId
			);
		}

		if (
			this.confirmationCardType === ConfigurationSummaryConfirmationCardTypeEnum.addToListSuccess ||
			this.confirmationCardType ===
				ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartAndListSuccess ||
			this.confirmationCardType ===
				ConfigurationSummaryConfirmationCardTypeEnum.addUnavailabeToCartFailAndListSuccess
		) {
			this.tracker?.sendAddToList(
				this.getShoppingItems(),
				this.shoppingProducts,
				this.getSettings(),
				designId
			);
		}
	}

	private getConfigurationSummaryShareDesignSheet(): RenderResult | null {
		if (!this.vpcCode || !this.shareDesignCard) {
			return null;
		}

		return html`
			<kompis-configuration-summary-share-design-sheet
				.theme="${this.theme}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.locale="${this.locale}"
				.vpcCode="${this.vpcCode}"
				.disableLinks="${this.disableLinks}"
				.visibleModal="${this.visibleModal}"
				.shareDesignCard="${this.shareDesignCard}"
				.screenshotUrl="${this.screenshotUrl}"
				.screenshotAlt="${this.screenshotAlt}"
				.productName="${this.productName}"
				.productDescription="${this.productDescription}"
				.analytics="${this.getSubComponentAnalytics()}"
				@modalClose="${({ detail }) => {
					this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail }));
				}}"
				@shareDesignCardStateChange="${({ detail }) =>
					this.dispatchEvent(
						new CustomEvent('shareDesignCardStateChange', { bubbles: true, detail })
					)}"
				@sendByEmailInput="${({ detail }) =>
					this.dispatchEvent(new CustomEvent('sendByEmailInput', { bubbles: true, detail }))}"
				@sendByEmailValidation="${({ detail }) =>
					this.dispatchEvent(new CustomEvent('sendByEmailValidation', { bubbles: true, detail }))}"
				@sendByEmailSend="${({ detail }) =>
					this.dispatchEvent(new CustomEvent('sendByEmailSend', { bubbles: true, detail }))}"
				@sendByEmailReset="${({ detail }) =>
					this.dispatchEvent(new CustomEvent('sendByEmailReset', { bubbles: true, detail }))}"
				@copyDesignCodeClick="${({ detail }) =>
					this.dispatchEvent(new CustomEvent('copyDesignCodeClick', { bubbles: true, detail }))}"
				@copyDesignCodeReset="${() =>
					this.dispatchEvent(new CustomEvent('copyDesignCodeReset', { bubbles: true }))}"
				@copyDesignLinkClick="${({ detail }) =>
					this.dispatchEvent(new CustomEvent('copyDesignLinkClick', { bubbles: true, detail }))}"
				@copyDesignLinkReset="${() =>
					this.dispatchEvent(new CustomEvent('copyDesignLinkReset', { bubbles: true }))}"
			>
			</kompis-configuration-summary-share-design-sheet>
		`;
	}

	private async connectTracker(): Promise<void> {
		if (this.analytics) {
			const locale = this.getLocaleForAnalyticsOrThrow();

			const reporter = this.analytics.__INTERNAL__reporter;
			if (reporter?.connected === false) {
				// Should use an existing reporter, but wait for it to be connected
				return;
			}

			// Use existing connected reporter, or the tracker should create a new one.
			const reporterId = reporter ? reporter.id : undefined;

			this.tracker = new ConfigurationSummaryTracker({
				...this.analytics,
				locale,
				settings: this.getSettings(),
				reporterId
			});

			await this.tracker?.connect();
			// Make sure child components with analytics connect their trackers when the id is available.
			this.requestUpdate();
		}
	}

	private disconnectTracker(): void {
		this.tracker?.disconnect();
		this.tracker = null;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-configuration-summary': ConfigurationSummary;
		'kompis-buy-in-store': BuyInStore;
	}
}
